import { FunctionComponent, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { faLifeRing } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";
import { InputField } from "@/components/InputField/InputField";
import { Modal } from "@/components/Modals/Modal";

import { useCreateSupportTicketMutation } from "@/pages/Private/redux/helpCenter/helpCenter.api";
import {
	HelpChatHistoryTurn,
	HelpChatPageContext,
} from "@/pages/Private/redux/helpCenter/helpCenter.schema";

interface SupportTicketModalProps {
	/** Subject prefilled from the triggering question (the user can edit it). */
	defaultSubject?: string;
	/** The page the user was on — forwarded so support knows the context. */
	pageContext?: HelpChatPageContext;
	/** The chat transcript that led to the ticket, forwarded for context. */
	transcript?: HelpChatHistoryTurn[];
	onClose: () => void;
}

const FormSchema = z.object({
	subject: z.string().trim().max(200).optional(),
	message: z.string().trim().min(1).max(8000),
});

type FormValues = z.infer<typeof FormSchema>;

// The help panel sits at z-[9999]; the dialog must stack above it (MUI defaults to 1000).
const TICKET_MODAL_Z_INDEX = 10000;

/**
 * Raise a support ticket from the AI help chat. The user only writes a subject + message; their
 * identity (name, email, company) is added server-side from the authenticated session, and the
 * chat transcript + current page are forwarded automatically so support has full context.
 */
export const SupportTicketModal: FunctionComponent<SupportTicketModalProps> = ({
	defaultSubject = "",
	pageContext,
	transcript,
	onClose,
}) => {
	const { t } = useTranslation();
	const [isSubmitted, setIsSubmitted] = useState(false);
	const [hasError, setHasError] = useState(false);

	const [createSupportTicket, { isLoading }] = useCreateSupportTicketMutation();

	const {
		control,
		handleSubmit,
		formState: { errors },
	} = useForm<FormValues>({
		resolver: zodResolver(FormSchema),
		defaultValues: { subject: defaultSubject, message: "" },
	});

	const onSubmit = async (values: FormValues) => {
		setHasError(false);

		try {
			await createSupportTicket({
				subject: values.subject,
				message: values.message,
				pageContext,
				transcript,
			}).unwrap();

			setIsSubmitted(true);
		} catch {
			setHasError(true);
		}
	};

	if (isSubmitted) {
		return (
			<Modal
				icon={<Icon className="text-brand-light" icon={faLifeRing} />}
				isOpened
				handleSave={onClose}
				submitButtonText={t("helpCenter.support.done")}
				title={t("helpCenter.support.sentTitle")}
				zIndex={TICKET_MODAL_Z_INDEX}
			>
				<p className="text-sm text-text-meta">{t("helpCenter.support.sentBody")}</p>
			</Modal>
		);
	}

	return (
		<Modal
			icon={<Icon className="text-brand-light" icon={faLifeRing} />}
			isOpened
			cancelButtonText={t("basics.cancel")}
			handleClose={onClose}
			handleSave={handleSubmit(onSubmit)}
			isLoading={isLoading}
			submitButtonText={t("helpCenter.support.submit")}
			title={t("helpCenter.support.title")}
			zIndex={TICKET_MODAL_Z_INDEX}
		>
			<div className="flex flex-col gap-1">
				<p className="mb-2 text-sm text-text-meta">{t("helpCenter.support.intro")}</p>

				<Controller
					control={control}
					name="subject"
					render={({ field }) => (
						<InputField
							label={t("helpCenter.support.subjectLabel")}
							name="subject"
							placeholder={t("helpCenter.support.subjectPlaceholder")}
							value={field.value ?? ""}
							handleChange={field.onChange}
						/>
					)}
				/>

				<Controller
					control={control}
					name="message"
					render={({ field }) => (
						<InputField
							error={errors.message ? t("helpCenter.support.messageRequired") : false}
							label={t("helpCenter.support.messageLabel")}
							multiline
							name="message"
							placeholder={t("helpCenter.support.messagePlaceholder")}
							rows={5}
							value={field.value ?? ""}
							handleChange={field.onChange}
						/>
					)}
				/>
				{errors.message && (
					<p className="text-xs text-status-error">{t("helpCenter.support.messageRequired")}</p>
				)}

				{hasError && (
					<p className="mt-2 text-sm text-status-error">{t("helpCenter.support.error")}</p>
				)}
			</div>
		</Modal>
	);
};
